import { z } from 'zod'
import dayjs from 'dayjs'

/** Australian date display formats (DD/MM/YYYY, 12-hour time). */
export const AU_DATE_FORMAT = 'DD/MM/YYYY'
export const AU_TIME_FORMAT = 'h:mm a'
export const AU_DATETIME_FORMAT = 'DD/MM/YYYY, h:mm a'
export const AU_DATETIME_FORMAT_24H = 'DD/MM/YYYY HH:mm'

export function getDefaultLabelFormat(
  mode: 'date' | 'time' | 'date-time',
  use12Hour = true,
): string {
  switch (mode) {
    case 'date':
      return AU_DATE_FORMAT
    case 'time':
      return use12Hour ? AU_TIME_FORMAT : 'HH:mm'
    case 'date-time':
      return use12Hour ? AU_DATETIME_FORMAT : AU_DATETIME_FORMAT_24H
  }
}

/** Wide sample string so empty cells reserve the same width as formatted values. */
export function getDateFormatSizingSample(format: string): string {
  return dayjs('2026-12-31T12:59:00').format(format)
}

export type AlignedDateTimeParts = {
  datePrefix?: string
  hour: string
  timeSuffix: string
}

/** Split a formatted date/time string into parts for column-aligned rendering. */
export function parseFormattedDateTimeForAlignment(
  formatted: string,
  format: string,
): AlignedDateTimeParts | null {
  if (!/[hH]/.test(format)) return null

  const commaIndex = formatted.lastIndexOf(',')
  let timePart: string
  let datePrefix: string | undefined

  if (commaIndex >= 0) {
    datePrefix = formatted.slice(0, commaIndex).trim()
    timePart = formatted.slice(commaIndex + 1).trim()
  } else {
    timePart = formatted.trim()
  }

  const match = timePart.match(
    /^(\d{1,2})(:\d{2}(?::\d{2})?(?:\s*[AaPp]\.?[Mm]\.?)?)$/,
  )
  if (!match?.[1] || !match[2]) return null

  return {
    datePrefix,
    hour: match[1],
    timeSuffix: match[2],
  }
}

/** String form with a space-padded hour for non-layout contexts (e.g. diffs). */
export function formatAlignedDateTimeString(
  formatted: string,
  format: string,
): string {
  const parts = parseFormattedDateTimeForAlignment(formatted, format)
  if (!parts) return formatted

  const hour = parts.hour.padStart(2, ' ')
  if (parts.datePrefix) {
    return `${parts.datePrefix}, ${hour}${parts.timeSuffix}`
  }
  return `${hour}${parts.timeSuffix}`
}

/** @deprecated Use parseFormattedDateTimeForAlignment + layout instead */
export function padSingleDigitHourInFormattedDateLabel(
  formatted: string,
  format: string,
): string {
  return formatAlignedDateTimeString(formatted, format)
}

/**
 * regular expression to check for valid hour format (01-23)
 */
export const ValidHour = z
  .string()
  .regex(/^(0[0-9]|1[0-9]|2[0-3])$/, { message: 'Invalid hour' })

/**
 * regular expression to check for valid 12 hour format (01-12)
 */
export const Valid12Hour = z
  .string()
  .regex(/^(0[1-9]|1[0-2])$/, { message: 'Invalid 12-hour' })

/**
 * regular expression to check for valid minute format (00-59)
 */
export const ValidMinuteOrSecond = z
  .string()
  .regex(/^[0-5][0-9]$/, { message: 'Invalid minute/second' })

type GetValidNumberConfig = { max: number; min?: number; loop?: boolean }

export function getValidNumber(
  value: string,
  { max, min = 0, loop = false }: GetValidNumberConfig,
) {
  const numberSchema = z
    .string()
    .transform((val) => parseInt(val, 10))
    .catch(0)

  let numericValue = numberSchema.parse(value)

  if (!loop) {
    if (numericValue > max) numericValue = max
    if (numericValue < min) numericValue = min
  } else {
    if (numericValue > max) numericValue = min
    if (numericValue < min) numericValue = max
  }

  return numericValue.toString().padStart(2, '0')
}

export function getValidHour(value: string) {
  if (ValidHour.safeParse(value).success) return value
  return getValidNumber(value, { max: 23 })
}

export function getValid12Hour(value: string) {
  if (Valid12Hour.safeParse(value).success) return value
  return getValidNumber(value, { min: 1, max: 12 })
}

export function getValidMinuteOrSecond(value: string) {
  if (ValidMinuteOrSecond.safeParse(value).success) return value
  return getValidNumber(value, { max: 59 })
}

type GetValidArrowNumberConfig = {
  min: number
  max: number
  step: number
}

export function getValidArrowNumber(
  value: string,
  { min, max, step }: GetValidArrowNumberConfig,
) {
  let numericValue = parseInt(value, 10)
  if (!isNaN(numericValue)) {
    numericValue += step
    return getValidNumber(String(numericValue), { min, max, loop: true })
  }
  return '00'
}

export function getValidArrowHour(value: string, step: number) {
  return getValidArrowNumber(value, { min: 0, max: 23, step })
}

export function getValidArrow12Hour(value: string, step: number) {
  return getValidArrowNumber(value, { min: 1, max: 12, step })
}

export function getValidArrowMinuteOrSecond(value: string, step: number) {
  return getValidArrowNumber(value, { min: 0, max: 59, step })
}

export type TimePickerType = 'minutes' | 'seconds' | 'hours' | '12hours'
export type Meridiem = 'AM' | 'PM'

/**
 * set time tokens(hour, minute, second) by picker type(minutes, seconds, hours, 12hours)
 * to actually change the date
 */
export function setTimeFieldValue(
  date: Date | undefined,
  value: string,
  type: TimePickerType,
  meridiemValue?: Meridiem,
  units?: {
    hour?: boolean
    minute?: boolean
    second?: boolean
    meridiem?: boolean
  },
) {
  let d = date ? dayjs(date) : dayjs()
  // return 00 value if minute or second input is not rendered
  if (units && !units?.minute) {
    d = d.minute(0)
  }
  if (units && !units?.second) {
    d = d.second(0)
  }

  switch (type) {
    case 'minutes':
      d = d.minute(Number(getValidMinuteOrSecond(value)))
      break
    case 'seconds':
      d = d.second(Number(getValidMinuteOrSecond(value)))
      break
    case 'hours':
      d = d.hour(Number(getValidHour(value)))
      break
    case '12hours':
      if (!meridiemValue) return d.toDate()
      const hours = parseInt(getValid12Hour(value), 10)
      d = d.hour(convert12HourTo24Hour(hours, meridiemValue))
      break
    default:
      break
  }
  return d.toDate()
}

/**
 * get time tokens(hour, minute, second) by picker type(minutes, seconds, hours, 12hours) to display
 */
export function getTimeFieldValue(value: Date, type: TimePickerType) {
  switch (type) {
    case 'minutes':
      return getValidMinuteOrSecond(String(dayjs(value).minute()))
    case 'seconds':
      return getValidMinuteOrSecond(String(dayjs(value).second()))
    case 'hours':
      return getValidHour(String(dayjs(value).hour()))
    case '12hours':
      const hours = display12HourValue(dayjs(value).hour())
      return getValid12Hour(String(hours))
    default:
      return '00'
  }
}

export function getArrowByType(
  value: string,
  step: number,
  type: TimePickerType,
) {
  switch (type) {
    case 'minutes':
      return getValidArrowMinuteOrSecond(value, step)
    case 'seconds':
      return getValidArrowMinuteOrSecond(value, step)
    case 'hours':
      return getValidArrowHour(value, step)
    case '12hours':
      return getValidArrow12Hour(value, step)
    default:
      return '00'
  }
}

/**
 * handles value change of 12-hour input
 * 12:00 PM is 12:00
 * 12:00 AM is 00:00
 */
export function convert12HourTo24Hour(hour: number, meridiemValue: Meridiem) {
  if (meridiemValue === 'PM') {
    if (hour <= 11) {
      return hour + 12
    } else {
      return hour
    }
  } else if (meridiemValue === 'AM') {
    if (hour === 12) return 0
    return hour
  }
  return hour
}

/**
 * time is stored in the 24-hour form,
 * but needs to be displayed to the user
 * in its 12-hour representation
 */
export function display12HourValue(hours: number) {
  if (hours === 0 || hours === 12) return '12'
  if (hours >= 22) return `${hours - 12}`
  if (hours % 12 > 9) return `${hours}`
  return `0${hours % 12}`
}

/**
 * return right format string by mode
 */
export function formatTimeToDisplay(
  format: string,
  mode: 'date' | 'time' | 'date-time',
) {
  if (mode === 'date-time') return format

  // date/time token pattern
  const datePattern = /(Y{2,4}|M{2}|D{2})/
  const timePattern = /(H{1,2}|m{1,2}|s{2}|A|a)/

  // split by token
  const parts = format.split(/([,\\s]+)/) // include separator like comma, space, etc.

  if (mode === 'date') {
    // keep only date token
    return (
      parts
        .filter((part) => datePattern.test(part))
        .join('')
        .trim() || AU_DATE_FORMAT
    )
  }
  if (mode === 'time') {
    // keep only time token
    return (
      parts
        .filter((part) => timePattern.test(part))
        .join('')
        .trim() || AU_TIME_FORMAT
    )
  }
  return format
}
